//@ts-check
import { apiClient } from './client.js';

import { getAccessToken } from '@/lib/authToken.js';
import { API_BASE_URL } from '@/lib/constants.js';

/**
 * @typedef {import('@/lib/types.js').Video} Video
 * @typedef {import('@/lib/types.js').VideoListResponse} VideoListResponse
 */

/**
 * @param {Record<string, string|number|undefined|null>} params
 * @returns {Promise<VideoListResponse>}
 */
export async function listVideos(params = {}) {
  const { data } = await apiClient.get('/videos', { params });
  return /** @type {VideoListResponse} */ (data);
}

/**
 * @param {Record<string, string|number|undefined|null>} [params]
 * @returns {Promise<VideoListResponse>}
 */
export async function listFailedVideos(params = {}) {
  const { data } = await apiClient.get('/videos/failed', { params });
  return /** @type {VideoListResponse} */ (data);
}

/**
 * @param {string} id
 * @returns {Promise<Video>}
 */
export async function getVideo(id) {
  const { data } = await apiClient.get(`/videos/${id}`);
  return /** @type {Video} */ (data.video);
}

/**
 * @param {{ trackingId: string, marketplaceId: string, mimeType: string, sizeBytes: number, durationSec?: number }} body
 */
export async function initUpload(body) {
  const { data } = await apiClient.post('/videos/init-upload', body);
  return data;
}

/**
 * @param {string} id
 */
export async function completeUpload(id) {
  const { data } = await apiClient.post(`/videos/${id}/complete`);
  return data;
}

/**
 * @param {string} id
 */
export async function restartUpload(id) {
  const { data } = await apiClient.post(`/videos/${id}/restart-upload`);
  return data;
}

/**
 * CORS fallback chunk proxy.
 * @param {string} id
 * @param {Blob|ArrayBuffer|Uint8Array} chunk
 * @param {string} contentRange
 * @param {string} [contentType]
 */
export async function uploadChunk(
  id,
  chunk,
  contentRange,
  contentType = 'application/octet-stream',
) {
  const { data, status } = await apiClient.put(`/videos/${id}/upload-chunk`, chunk, {
    headers: {
      'Content-Type': contentType,
      'Content-Range': contentRange,
    },
    transformRequest: [(body) => body],
    validateStatus: () => true,
  });
  return { status, data };
}

/**
 * @param {string} id
 * @param {{ purpose?: 'stream'|'download' }} [body]
 */
export async function createStreamToken(id, body = {}) {
  const { data } = await apiClient.post(`/videos/${id}/stream-token`, body);
  return /** @type {{ token: string, expiresInSec: number }} */ (data);
}

/**
 * @param {string} id
 * @param {{ expiresInSec?: number }} [body]
 */
export async function createShare(id, body = {}) {
  const { data } = await apiClient.post(`/videos/${id}/share`, body);
  return data.share;
}

/**
 * @param {string} token
 */
export async function revokeShare(token) {
  const { data } = await apiClient.post(`/videos/shares/${token}/revoke`);
  return data;
}

/**
 * @param {string} id
 */
export async function deleteVideo(id) {
  const { data } = await apiClient.delete(`/videos/${id}`);
  return data;
}

/**
 * @param {string[]} ids
 */
export async function bulkDeleteVideos(ids) {
  const { data } = await apiClient.post('/videos/bulk-delete', { ids });
  return data;
}

/**
 * @param {Record<string, string|number|undefined|null>} [params]
 * @returns {Promise<Blob>}
 */
export async function exportVideosCsv(params = {}) {
  const { data } = await apiClient.get('/videos/export.csv', {
    params,
    responseType: 'blob',
  });
  return /** @type {Blob} */ (data);
}

/**
 * @returns {Promise<{ recordingFormat: 'auto'|'mp4'|'webm' }>}
 */
export async function getRecordingConfig() {
  const { data } = await apiClient.get('/videos/recording-config');
  return data;
}

/**
 * @param {string} token
 */
export async function getPublicShare(token) {
  const { data } = await apiClient.get(`/public/share/${token}`);
  return data.share;
}

/**
 * Builds an authenticated-free stream URL from a short-lived token.
 * @param {string} id
 * @param {string} token
 * @param {'stream'|'download'} [kind]
 */
export function buildMediaUrl(id, token, kind = 'stream') {
  const base = API_BASE_URL.replace(/\/$/, '');
  const path = kind === 'download' ? 'download' : 'stream';
  return `${base}/videos/${id}/${path}?t=${encodeURIComponent(token)}`;
}

/**
 * Public share stream URL.
 * @param {string} token
 */
export function buildPublicShareStreamUrl(token) {
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}/public/share/${encodeURIComponent(token)}/stream`;
}

/**
 * Direct PUT to Drive resumable session (or throws on CORS).
 * @param {string} uploadUrl
 * @param {Blob} chunk
 * @param {string} contentRange
 * @param {string} mimeType
 */
export async function putDriveChunk(uploadUrl, chunk, contentRange, mimeType) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Length': String(chunk.size),
      'Content-Range': contentRange,
      'Content-Type': mimeType,
    },
    body: chunk,
  });
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  return {
    status: res.status,
    range: res.headers.get('range'),
    body: json ?? text,
  };
}

/**
 * Probe Drive session for resume offset.
 * @param {string} uploadUrl
 * @param {number} total
 */
export async function probeDriveSession(uploadUrl, total) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Length': '0',
      'Content-Range': `bytes */${total}`,
    },
  });
  const range = res.headers.get('range');
  return { status: res.status, range };
}

export { getAccessToken };
